import type { AudioEngine } from "../AudioEngine";
import type { BeatStep, ProceduralBeat } from "../ProceduralBeat";
import type { Composer } from "../composer/Composer";
import type { BarContext, SectionKind } from "../composer/types";

// Melodic-dubstep wobble bass. Softer than the brostep variant: lower
// distortion, smoother filter sweep, slower default LFO rate. Active
// only during `drop` (and optionally `bridge`) sections so it stays a
// signature texture instead of a constant grind.
//
// Always-on graph layout:
//   [3 saw oscs] -> unison -> filter -> distortion -> volume -> level -> dest
//   lfoOsc -> lfoGain -> filter.frequency
const FILTER_BASE_HZ = 90;
const FILTER_SWEEP_HZ = 2400;
const UNISON_DETUNE_CENTS = 12;
const DISTORTION_AMOUNT = 25; // softer growl for melodic dubstep

// LFO rates (in 1/Nth-note units) cycled across bars in the active
// section. Slower rates than brostep -- closer to "yoy yoy" than
// "rrrrrrrr" so the wobble dances under the lead instead of dominating.
const LFO_RATES_PER_BAR = [8, 8, 4, 8];

const ACTIVE_SECTIONS: SectionKind[] = ["drop", "bridge"];

export class WobbleBass {
  private engine: AudioEngine;
  private beat: ProceduralBeat;
  private composer: Composer | null = null;

  private oscs: OscillatorNode[] = [];
  private unison: GainNode | null = null;
  private filter: BiquadFilterNode | null = null;
  private distortion: WaveShaperNode | null = null;
  private volume: GainNode | null = null;
  private level: GainNode | null = null;
  private lfoOsc: OscillatorNode | null = null;
  private lfoGain: GainNode | null = null;

  private rootHz = 55;
  // External level the MusicDirector sets via setLevel(). Section gate
  // multiplies this so the wobble auto-mutes outside drop/bridge.
  private targetGain = 0;
  private currentSection: SectionKind = "intro";
  private playing = false;
  private barIndex = 0;
  private unsubBeat: (() => void) | null = null;
  private unsubBar: (() => void) | null = null;

  constructor(engine: AudioEngine, beat: ProceduralBeat) {
    this.engine = engine;
    this.beat = beat;
  }

  bindComposer(composer: Composer): void {
    if (this.composer === composer) return;
    if (this.unsubBar) this.unsubBar();
    this.composer = composer;
    this.unsubBar = composer.onBar.on((ctx) => this.onBar(ctx));
    const cur = composer.getCurrentContext();
    if (cur) {
      this.adoptChord(cur);
      this.currentSection = cur.section;
      this.applyEffectiveLevel(/*instant=*/ true);
    }
  }

  start(): void {
    if (this.playing) return;
    const ctx = this.engine.context;
    const dest = this.engine.musicDestination ?? this.engine.destination;
    if (!ctx || !dest) return;

    this.unison = ctx.createGain();
    this.unison.gain.value = 0.55;

    this.oscs = [];
    const detunes = [-UNISON_DETUNE_CENTS, 0, UNISON_DETUNE_CENTS];
    for (const d of detunes) {
      const o = ctx.createOscillator();
      o.type = "sawtooth";
      o.frequency.value = this.rootHz;
      o.detune.value = d;
      const voice = ctx.createGain();
      voice.gain.value = 0.34;
      o.connect(voice).connect(this.unison);
      this.oscs.push(o);
    }

    this.filter = ctx.createBiquadFilter();
    this.filter.type = "lowpass";
    this.filter.Q.value = 7;
    this.filter.frequency.value = FILTER_BASE_HZ;

    this.distortion = ctx.createWaveShaper();
    this.distortion.curve = makeDistortionCurve(DISTORTION_AMOUNT);
    this.distortion.oversample = "2x";

    this.volume = ctx.createGain();
    this.volume.gain.value = 0.6;

    this.level = ctx.createGain();
    this.level.gain.value = 0;

    this.unison
      .connect(this.filter)
      .connect(this.distortion)
      .connect(this.volume)
      .connect(this.level)
      .connect(dest);

    // Smoother LFO: triangle for melodic dubstep so the wobble talks
    // instead of slapping. Brostep uses sawtooth for the harder edge.
    this.lfoOsc = ctx.createOscillator();
    this.lfoOsc.type = "triangle";
    this.lfoOsc.frequency.value = this.lfoRateForRate(
      LFO_RATES_PER_BAR[0]!,
      this.beat.getBpm()
    );

    this.lfoGain = ctx.createGain();
    this.lfoGain.gain.value = FILTER_SWEEP_HZ;

    this.lfoOsc.connect(this.lfoGain).connect(this.filter.frequency);

    const t = ctx.currentTime;
    for (const o of this.oscs) o.start(t);
    this.lfoOsc.start(t);

    this.unsubBeat = this.beat.onStep.on((s) => this.onBeat(s));
    this.playing = true;
  }

  stop(): void {
    if (!this.playing) return;
    this.unsubBeat?.();
    this.unsubBeat = null;
    if (this.unsubBar) {
      this.unsubBar();
      this.unsubBar = null;
    }
    const t = this.engine.context?.currentTime ?? 0;
    for (const o of this.oscs) {
      try { o.stop(t + 0.05); } catch { /* not started */ }
      try { o.disconnect(); } catch { /* gone */ }
    }
    this.oscs.length = 0;
    try { this.lfoOsc?.stop(t + 0.05); } catch { /* not started */ }
    for (const n of [this.unison, this.filter, this.distortion, this.volume, this.level, this.lfoOsc, this.lfoGain]) {
      try { n?.disconnect(); } catch { /* gone */ }
    }
    this.unison = null;
    this.filter = null;
    this.distortion = null;
    this.volume = null;
    this.level = null;
    this.lfoGain = null;
    this.lfoOsc = null;
    this.composer = null;
    this.playing = false;
  }

  setPitch(rootHz: number): void {
    let hz = rootHz;
    while (hz > 90) hz *= 0.5;
    while (hz < 35) hz *= 2;
    this.rootHz = hz;
    this.repitchOscs();
  }

  setLevel(gain01: number, rampMs = 600): void {
    this.targetGain = Math.max(0, Math.min(1, gain01));
    this.applyEffectiveLevel(rampMs <= 50);
    if (!this.level || !this.engine.context) return;
    const t = this.engine.context.currentTime;
    const g = this.level.gain;
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(this.effectiveLevel(), t + rampMs / 1000);
  }

  private onBar(ctx: BarContext): void {
    this.adoptChord(ctx);
    this.currentSection = ctx.section;
    this.applyEffectiveLevel(/*instant=*/ false);
  }

  private adoptChord(ctx: BarContext): void {
    let hz = ctx.chord.bassHz;
    while (hz > 90) hz *= 0.5;
    while (hz < 35) hz *= 2;
    this.rootHz = hz;
    this.repitchOscs();
  }

  private repitchOscs(): void {
    if (!this.engine.context || this.oscs.length === 0) return;
    const t = this.engine.context.currentTime;
    for (const o of this.oscs) {
      o.frequency.cancelScheduledValues(t);
      o.frequency.setTargetAtTime(this.rootHz, t, 0.04);
    }
  }

  // Section gate * external target. Wobble fully off during intro/verse
  // /build/outro so the mix doesn't get muddy with constant low growl.
  private effectiveLevel(): number {
    if (!ACTIVE_SECTIONS.includes(this.currentSection)) return 0;
    return this.targetGain;
  }

  private applyEffectiveLevel(instant: boolean): void {
    if (!this.level || !this.engine.context) return;
    const t = this.engine.context.currentTime;
    const g = this.level.gain;
    const target = this.effectiveLevel();
    g.cancelScheduledValues(t);
    g.setValueAtTime(g.value, t);
    g.linearRampToValueAtTime(target, t + (instant ? 0.05 : 0.4));
  }

  private onBeat(step: BeatStep): void {
    if (!this.lfoOsc || !this.engine.context) return;
    if (!step.isBarStart) return;
    const rate = LFO_RATES_PER_BAR[this.barIndex % LFO_RATES_PER_BAR.length]!;
    this.barIndex += 1;
    const hz = this.lfoRateForRate(rate, step.bpm);
    const t = step.time;
    this.lfoOsc.frequency.cancelScheduledValues(t);
    this.lfoOsc.frequency.setTargetAtTime(hz, t, 0.02);
  }

  private lfoRateForRate(n: number, bpm: number): number {
    return (bpm / 60) * (n / 4);
  }
}

function makeDistortionCurve(amount: number): Float32Array<ArrayBuffer> {
  const samples = 1024;
  const buffer = new ArrayBuffer(samples * Float32Array.BYTES_PER_ELEMENT);
  const curve = new Float32Array(buffer);
  const k = amount;
  for (let i = 0; i < samples; i++) {
    const x = (i * 2) / samples - 1;
    curve[i] = Math.tanh(k * x) / Math.tanh(k);
  }
  return curve;
}
